import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';
import { confirmDelete, handleDeleteError } from '../utils/deleteHelper';

const EMPTY = { name:'', employee_id:'', phone:'', license_number:'', license_expiry:'', status:'available', company_id:'' };

const isExpiringSoon = (dateStr) => {
  if (!dateStr) return false;
  const expiry = new Date(dateStr);
  const now = new Date();
  const diff = (expiry - now) / (1000 * 60 * 60 * 24);
  return diff < 30;
};

// Status badge colors + labels
const STATUS_META = {
  available: { color: 'bg-green-100 text-green-700',  label: 'Available' },
  busy:      { color: 'bg-yellow-100 text-yellow-700', label: 'Busy (Planned)' },
  on_trip:   { color: 'bg-blue-100 text-blue-700',    label: 'On Trip' },
  on_leave:  { color: 'bg-orange-100 text-orange-700', label: 'On Leave' },
  inactive:  { color: 'bg-gray-100 text-gray-500',    label: 'Inactive' },
  // Legacy value support
  active:    { color: 'bg-green-100 text-green-700',  label: 'Available' },
};

// Statuses that are locked by an active trip — cannot be manually edited
const LOCKED_STATUSES = ['busy', 'on_trip'];

export default function Drivers() {
  const { user } = useAuth();
  const [drivers, setDrivers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [editingDriver, setEditingDriver] = useState(null); // full driver object for lock check
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const isAdmin = user?.role === 'admin';

  const load = async () => {
    if (!user) return;
    setLoading(true);
    try { const r = await api.get('/api/drivers'); setDrivers(r.data); }
    catch (err) { console.error('drivers:', err); setDrivers([]); }
    try { const r = await api.get('/api/employees/active'); setEmployees(r.data); }
    catch (err) { console.error('employees:', err); setEmployees([]); }
    if (isAdmin) {
      try { const r = await api.get('/api/companies'); setCompanies(r.data); }
      catch { setCompanies([]); }
    }
    setLoading(false);
  };

  useEffect(() => { if (user) load(); }, [user?.role]);

  const filtered = drivers.filter(d =>
    d.name.toLowerCase().includes(search.toLowerCase()) ||
    (d.phone||'').includes(search) ||
    (d.license_number||'').toLowerCase().includes(search.toLowerCase())
  );

  const getEmployeeName = (id) => employees.find(e => e.id === Number(id))?.name || null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, employee_id: form.employee_id ? Number(form.employee_id) : null };
      if (editing) await api.put(`/api/drivers/${editing}`, payload);
      else await api.post('/api/drivers', payload);
      setShowForm(false); setForm(EMPTY); setEditing(null); setEditingDriver(null); load();
    } catch (err) { alert(err.response?.data?.error || 'Save failed'); }
  };

  const handleEdit = (d) => {
    setForm({
      name: d.name, employee_id: d.employee_id || '', phone: d.phone || '',
      license_number: d.license_number || '', license_expiry: d.license_expiry || '',
      status: d.status || 'available',
      company_id: d.company_id ? String(d.company_id) : ''
    });
    setEditing(d.id);
    setEditingDriver(d);
    setShowForm(true);
  };

  const handleDelete = async (id, name) => {
    if (!confirmDelete('driver', name)) return;
    try { await api.delete(`/api/drivers/${id}`); load(); }
    catch (err) { handleDeleteError(err); }
  };

  // Whether status dropdown should be locked for the driver being edited
  const isStatusLocked = editingDriver && LOCKED_STATUSES.includes(editingDriver.status);

  // Summary counts
  const counts = drivers.reduce((acc, d) => {
    const s = d.status === 'active' ? 'available' : d.status;
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Driver Management</h1>
          <p className="text-gray-500 text-sm">{drivers.length} drivers registered</p>
        </div>
        <button onClick={() => { setForm(EMPTY); setEditing(null); setEditingDriver(null); setShowForm(true); }}
          className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-lg text-sm font-medium">
          ➕ Add Driver
        </button>
      </div>

      {/* Status summary pills */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 flex flex-wrap gap-3 items-center">
        <input type="search" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search by name, phone, license..."
          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[180px]" />
        <div className="flex gap-2 flex-wrap">
          {[
            ['available','Available','bg-green-100 text-green-700'],
            ['busy','Busy','bg-yellow-100 text-yellow-700'],
            ['on_trip','On Trip','bg-blue-100 text-blue-700'],
            ['on_leave','On Leave','bg-orange-100 text-orange-700'],
            ['inactive','Inactive','bg-gray-100 text-gray-500'],
          ].map(([key, label, cls]) => counts[key] ? (
            <span key={key} className={`px-2 py-1 rounded-full text-xs font-semibold ${cls}`}>
              {label}: {counts[key]}
            </span>
          ) : null)}
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">{editing ? 'Edit Driver' : 'Add Driver'}</h2>
              <button onClick={() => { setShowForm(false); setEditing(null); setEditingDriver(null); }}
                className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Driver Name *</label>
                <input type="text" value={form.name} onChange={e => setForm({...form,name:e.target.value})} required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Linked Employee (Optional)</label>
                <select value={form.employee_id} onChange={e => setForm({...form,employee_id:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">— Not Linked —</option>
                  {employees.map(e => <option key={e.id} value={e.id}>{e.name} ({e.department||'No Dept'})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input type="text" value={form.phone} onChange={e => setForm({...form,phone:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">License Number</label>
                <input type="text" value={form.license_number} onChange={e => setForm({...form,license_number:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">License Expiry</label>
                <input type="date" value={form.license_expiry} onChange={e => setForm({...form,license_expiry:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                  {isStatusLocked && (
                    <span className="ml-2 text-xs text-orange-600 font-normal">🔒 Locked — active trip</span>
                  )}
                </label>
                {isStatusLocked ? (
                  /* Read-only when driver is busy/on_trip — managed by trip lifecycle */
                  <div className="w-full border border-gray-100 bg-gray-50 rounded-lg px-3 py-2 text-sm text-gray-500">
                    {STATUS_META[form.status]?.label || form.status} — complete or cancel the trip to change
                  </div>
                ) : (
                  <select value={form.status} onChange={e => setForm({...form,status:e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="available">Available</option>
                    <option value="on_leave">On Leave</option>
                    <option value="inactive">Inactive</option>
                  </select>
                )}
              </div>
              {/* Company assignment */}
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Company *</label>
                {isAdmin ? (
                  <select value={form.company_id} onChange={e => setForm({...form,company_id:e.target.value})} required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">— Select Company —</option>
                    {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                ) : (
                  <input type="text" disabled value={user?.company_name || 'Your Company'}
                    className="w-full border border-gray-100 bg-gray-50 rounded-lg px-3 py-2 text-sm text-gray-500" />
                )}
              </div>
              <div className="sm:col-span-2 flex gap-3 pt-2">
                <button type="submit" className="bg-blue-700 hover:bg-blue-800 text-white px-6 py-2 rounded-lg font-medium">
                  {editing ? 'Update' : 'Add Driver'}
                </button>
                <button type="button" onClick={() => { setShowForm(false); setEditing(null); setEditingDriver(null); }}
                  className="border border-gray-300 px-6 py-2 rounded-lg hover:bg-gray-50">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-400">
            <div className="text-4xl animate-pulse mb-2">🧑‍✈️</div>
            <p>Loading drivers...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <div className="text-5xl mb-3">🧑‍✈️</div>
            <p>No drivers found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Driver Name','Linked Employee',isAdmin?'Company':'','Phone','License No.','Expiry','Status','Actions'].filter(Boolean).map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map(d => {
                  const expiringSoon = isExpiringSoon(d.license_expiry);
                  const statusMeta = STATUS_META[d.status] || { color: 'bg-gray-100 text-gray-500', label: d.status };
                  const locked = LOCKED_STATUSES.includes(d.status);
                  return (
                    <tr key={d.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 font-medium text-gray-800">
                        {d.name}
                        {locked && <span className="ml-1 text-orange-400 text-xs" title="Status locked — active trip">🔒</span>}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{d.employee_id ? getEmployeeName(d.employee_id)||'Employee #'+d.employee_id : '—'}</td>
                      {isAdmin && <td className="px-4 py-3 text-gray-600 text-xs">{d.company_name || <span className="text-red-400 font-semibold">⚠ No Company</span>}</td>}
                      <td className="px-4 py-3 text-gray-600">{d.phone||'—'}</td>
                      <td className="px-4 py-3 font-mono text-gray-600">{d.license_number||'—'}</td>
                      <td className="px-4 py-3">
                        <span className={expiringSoon ? 'text-red-600 font-semibold' : 'text-gray-600'}>
                          {d.license_expiry ? new Date(d.license_expiry).toLocaleDateString('en-PK') : '—'}
                          {expiringSoon && ' ⚠️'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusMeta.color}`}>
                          {statusMeta.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button onClick={() => handleEdit(d)} className="text-blue-600 hover:text-blue-800 text-xs font-medium">✏️ Edit</button>
                          {isAdmin && <button onClick={() => handleDelete(d.id, d.name)} className="text-red-500 hover:text-red-700 text-xs font-medium">🗑️ Remove</button>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
